/**
 * Shared utilities for {@link HolidayRow}, {@link HolidayCard} and {@link HolidaysDataTable}.
 */
import type { Holiday } from '@/types/holiday.types';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import {
  HOLIDAY_TYPE_CONFIG,
  type HolidayFilters,
  type HolidayTypeConfig,
} from './holiday-configs';

/** Row / card display fields derived once from a holiday. */
export interface DerivedHolidayDisplay {
  typeConfig: HolidayTypeConfig;
  TypeIcon: HolidayTypeConfig['icon'];
  zoneLabel: string | null;
  establishmentLabel: string | null;
  startDateShort: string;
  endDateShort: string;
  startDateLong: string;
  endDateLong: string;
}

/** Detail-panel extras on top of {@link DerivedHolidayDisplay}. */
export interface DerivedHolidayDetailDisplay extends DerivedHolidayDisplay {
  typeColorName: HolidayTypeConfig['colorName'];
  vacationZoneDetail: string | null;
  createdAtLabel: string | undefined;
  updatedAtLabel: string | undefined;
}

/** Short French date (`dd MMM yyyy`). */
export function formatHolidayDateShort(date: string | Date): string {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Long French date (`dd MMMM yyyy`). */
export function formatHolidayDateLong(date: string | Date): string {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Badge + icon config for a holiday type. */
export function getHolidayTypeConfig(type: string): HolidayTypeConfig {
  return HOLIDAY_TYPE_CONFIG[type];
}

/** Zone chip label; `null` when no code. */
export function formatHolidayZoneLabel(code: string | undefined | null): string | null {
  return code ? `Zone ${code}` : null;
}

/** Establishment chip label; falls back when only the id is present. */
export function formatHolidayEstablishmentLabel(
  holiday: Pick<Holiday, 'establishmentName' | 'establishmentId'>,
): string | null {
  if (!holiday.establishmentName && !holiday.establishmentId) return null;
  return holiday.establishmentName || 'Établissement';
}

/** Zone detail line (`name (code)`). */
export function formatHolidayVacationZoneDetail(
  zone: { name: string; code: string } | null | undefined,
): string | null {
  if (!zone) return null;
  return `${zone.name} (${zone.code})`;
}

/** Maps UI filters to API dateFrom/dateTo (presets: current week / month / year). */
export function resolveHolidayDateRange(filters: HolidayFilters): {
  dateFrom?: Date;
  dateTo?: Date;
} {
  return resolveListDateRange(filters, { periodEnd: 'endOfPeriod' });
}

/** Map UI filters + pagination to holiday list params. */
export function toHolidayListParams(
  filters: HolidayFilters,
  page: number,
  pageSize: number,
): {
  page: number;
  pageSize: number;
  types?: HolidayFilters['types'];
  dateFrom?: Date;
  dateTo?: Date;
  vacationZoneId?: string;
} {
  const { dateFrom, dateTo } = resolveHolidayDateRange(filters);
  return {
    page,
    pageSize,
    types: filters.types.length > 0 ? filters.types : undefined,
    dateFrom,
    dateTo,
    vacationZoneId: filters.vacationZoneId || undefined,
  };
}

/** Whether any type / date-range / custom date / zone filter is set. */
export function hasHolidayActiveFilters(filters: HolidayFilters): boolean {
  return (
    filters.types.length > 0 ||
    filters.dateRange !== 'all' ||
    filters.customDateFrom !== undefined ||
    filters.customDateTo !== undefined ||
    Boolean(filters.vacationZoneId)
  );
}

/** Local `YYYY-MM-DD` for {@link DatePicker} filter values. */
export function toHolidayFilterDateString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse a filter start date at local midnight. */
export function parseHolidayFilterStartDate(val: string): Date {
  const [year, month, day] = val.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0);
}

/** Parse a filter end date at local end-of-day. */
export function parseHolidayFilterEndDate(val: string): Date {
  const [year, month, day] = val.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

/** Shared row / card display payload. */
export function deriveHolidayDisplay(holiday: Holiday): DerivedHolidayDisplay {
  const typeConfig = getHolidayTypeConfig(holiday.type);
  return {
    typeConfig,
    TypeIcon: typeConfig.icon,
    zoneLabel: formatHolidayZoneLabel(holiday.vacationZone?.code),
    establishmentLabel: formatHolidayEstablishmentLabel(holiday),
    startDateShort: formatHolidayDateShort(holiday.startDate),
    endDateShort: formatHolidayDateShort(holiday.endDate),
    startDateLong: formatHolidayDateLong(holiday.startDate),
    endDateLong: formatHolidayDateLong(holiday.endDate),
  };
}

/** Detail-panel display payload (includes created / updated labels). */
export function deriveHolidayDetailDisplay(holiday: Holiday): DerivedHolidayDetailDisplay {
  return {
    ...deriveHolidayDisplay(holiday),
    typeColorName: getHolidayTypeConfig(holiday.type).colorName ?? 'color-2',
    vacationZoneDetail: formatHolidayVacationZoneDetail(holiday.vacationZone),
    createdAtLabel: holiday.createdAt ? formatHolidayDateLong(holiday.createdAt) : undefined,
    updatedAtLabel: holiday.updatedAt ? formatHolidayDateLong(holiday.updatedAt) : undefined,
  };
}

/** Recurrence / establishment summary — aligned with Excel export. */
export function formatHolidayOptionsText(
  holiday: Pick<Holiday, 'isRecurringYearly' | 'establishmentName'>,
): string {
  const parts: string[] = [];
  parts.push(holiday.isRecurringYearly ? 'Récurrent chaque année' : 'Non récurrent');
  const est = holiday.establishmentName?.trim();
  if (est) parts.push(`Établissement : ${est}`);
  return parts.join('; ');
}
